import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { colors, spacing, borderRadius, Icon, Alert } from '@tipsy/design-system';
import { cardsApi } from '../api';
import type { Card, CardDetails, Transaction } from '../api';
import type { WalletStackParamList } from '../navigation/MainNavigator';
import { CardBackground } from '../components/CardBackground';

type NavigationProp = NativeStackNavigationProp<WalletStackParamList, 'VirtualCard'>;
type VirtualCardRouteProp = RouteProp<WalletStackParamList, 'VirtualCard'>;

const VirtualCardScreen = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<VirtualCardRouteProp>();
  const { cardId } = route.params;
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [card, setCard] = useState<Card | null>(null);
  const [cardDetails, setCardDetails] = useState<CardDetails | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isFreezeLoading, setIsFreezeLoading] = useState(false);

  const fetchCard = useCallback(
    async (showRefresh = false) => {
      try {
        if (showRefresh) {
          setIsRefreshing(true);
        }

        const cardData = await cardsApi.getCard(cardId);
        setCard(cardData);

        if (cardData) {
          const txResponse = await cardsApi.getTransactions(cardId, 10);
          setTransactions(txResponse.transactions);
        }
      } catch {
        Alert.alert('Error', 'Failed to load card');
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [cardId],
  );

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleShowDetails = async () => {
    if (showDetails) {
      setShowDetails(false);
      setCardDetails(null);
      return;
    }

    try {
      setIsDetailsLoading(true);
      const details = await cardsApi.getCardDetails(cardId);
      setCardDetails(details);
      setShowDetails(true);
    } catch {
      Alert.alert('Error', 'Failed to load card details');
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleFreezeCard = async () => {
    if (!card) return;

    const newStatus = card.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'inactive' ? 'freeze' : 'unfreeze';

    Alert.alert(
      `${action.charAt(0).toUpperCase() + action.slice(1)} Card`,
      `Are you sure you want to ${action} your card?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: action.charAt(0).toUpperCase() + action.slice(1),
          style: newStatus === 'inactive' ? 'destructive' : 'default',
          onPress: async () => {
            try {
              setIsFreezeLoading(true);
              const updatedCard = await cardsApi.updateCardStatus(cardId, { status: newStatus });
              setCard(updatedCard);
            } catch {
              Alert.alert('Error', `Failed to ${action} card`);
            } finally {
              setIsFreezeLoading(false);
            }
          },
        },
      ],
    );
  };

  const handleViewTransactions = () => {
    navigation.navigate('CardTransactions', { cardId });
  };

  const formatCardNumber = (number: string) => {
    return number.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatAmount = (amount: number, currency: string) => {
    const absAmount = Math.abs(amount) / 100;
    const sign = amount < 0 ? '-' : '+';
    const symbol = currency === 'usd' ? '$' : currency === 'gbp' ? '\u00A3' : '\u20AC';
    return `${sign}${symbol}${absAmount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!card) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyTitle}>No Virtual Card</Text>
          <Text style={styles.emptyText}>
            You don't have a virtual card yet. Create one to start spending your tips.
          </Text>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => navigation.navigate('CardSetup')}
          >
            <Text style={styles.primaryButtonText}>Get Virtual Card</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const isFrozen = card.status === 'inactive';

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={() => fetchCard(true)}
            tintColor={colors.interactive.primary}
          />
        }
      >
        {/* Card Visual */}
        <CardBackground
          variant={isFrozen ? 'frozen' : 'default'}
          height={200}
          style={isFrozen ? { ...styles.cardWrapper, ...styles.cardFrozen } : styles.cardWrapper}
        >
          <View style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardBrand}>{card.brand}</Text>
              <Text style={styles.cardType}>Virtual</Text>
            </View>

            <View style={styles.cardNumber}>
              {showDetails && cardDetails ? (
                <Text style={styles.cardNumberText}>{formatCardNumber(cardDetails.number)}</Text>
              ) : (
                <Text style={styles.cardNumberText}>**** **** **** {card.last4}</Text>
              )}
            </View>

            <View style={styles.cardFooter}>
              <View>
                <Text style={styles.cardLabel}>EXPIRES</Text>
                <Text style={styles.cardValue}>
                  {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                </Text>
              </View>
              <View>
                <Text style={styles.cardLabel}>CVC</Text>
                <Text style={styles.cardValue}>
                  {showDetails && cardDetails ? cardDetails.cvc : '***'}
                </Text>
              </View>
              <View>
                <Text style={styles.cardLabel}>STATUS</Text>
                <Text style={[styles.cardValue, isFrozen && styles.frozenText]}>
                  {isFrozen ? 'Frozen' : 'Active'}
                </Text>
              </View>
            </View>

            {isFrozen && (
              <View style={styles.frozenOverlay}>
                <Text style={styles.frozenOverlayText}>FROZEN</Text>
              </View>
            )}
          </View>
        </CardBackground>

        {/* Actions */}
        <View style={styles.actionsContainer}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleShowDetails}
            disabled={isDetailsLoading}
          >
            {isDetailsLoading ? (
              <ActivityIndicator color={colors.text.primary} size="small" />
            ) : (
              <>
                <Icon
                  name={showDetails ? 'visibility-off' : 'visibility'}
                  size={24}
                  color={colors.text.primary}
                  style={styles.actionIcon}
                />
                <Text style={styles.actionText}>{showDetails ? 'Hide' : 'Show'}</Text>
              </>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleFreezeCard}
            disabled={isFreezeLoading}
          >
            {isFreezeLoading ? (
              <ActivityIndicator color={colors.text.primary} size="small" />
            ) : (
              <>
                <Icon
                  name={isFrozen ? 'lock-open' : 'lock'}
                  size={24}
                  color={colors.text.primary}
                  style={styles.actionIcon}
                />
                <Text style={styles.actionText}>{isFrozen ? 'Unfreeze' : 'Freeze'}</Text>
              </>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.actionButton} onPress={handleViewTransactions}>
            <Icon
              name="receipt-long"
              size={24}
              color={colors.text.primary}
              style={styles.actionIcon}
            />
            <Text style={styles.actionText}>History</Text>
          </TouchableOpacity>
        </View>

        {/* Recent Transactions */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Recent Transactions</Text>
            {transactions.length > 0 && (
              <TouchableOpacity onPress={handleViewTransactions}>
                <Text style={styles.seeAllText}>See All</Text>
              </TouchableOpacity>
            )}
          </View>

          {transactions.length > 0 ? (
            <View style={styles.transactionsList}>
              {transactions.slice(0, 5).map((tx) => (
                <View key={tx.id} style={styles.transactionItem}>
                  <View style={styles.transactionInfo}>
                    <Text style={styles.transactionMerchant}>
                      {tx.merchantName || 'Unknown Merchant'}
                    </Text>
                    <Text style={styles.transactionDate}>{formatDate(tx.createdAt)}</Text>
                  </View>
                  <Text
                    style={[
                      styles.transactionAmount,
                      tx.amount > 0 && styles.transactionAmountPositive,
                    ]}
                  >
                    {formatAmount(tx.amount, tx.currency)}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyTransactions}>
              <Text style={styles.emptyTransactionsText}>No transactions yet</Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.base,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: spacing.md,
  },
  emptyText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 24,
  },
  primaryButton: {
    backgroundColor: colors.interactive.primary,
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing['2xl'],
  },
  primaryButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.text.inverse,
  },
  cardWrapper: {
    marginTop: spacing.lg,
  },
  cardContent: {
    flex: 1,
    padding: spacing.lg,
    justifyContent: 'space-between',
  },
  cardFrozen: {
    opacity: 0.7,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardBrand: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ffe998',
  },
  cardType: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffe998',
    opacity: 0.8,
  },
  cardNumber: {
    marginVertical: spacing.xl,
  },
  cardNumberText: {
    fontSize: 22,
    fontWeight: '500',
    color: '#ffe998',
    letterSpacing: 2,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: '500',
    color: 'rgba(255, 233, 152, 0.6)',
    marginBottom: spacing.xs,
  },
  cardValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffe998',
  },
  frozenText: {
    color: '#FF6B6B',
  },
  frozenOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: borderRadius.xl,
  },
  frozenOverlayText: {
    fontSize: 32,
    fontWeight: '800',
    color: 'rgba(255, 255, 255, 0.3)',
    letterSpacing: 4,
    transform: [{ rotate: '-15deg' }],
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: spacing.xl,
    marginBottom: spacing.lg,
  },
  actionButton: {
    alignItems: 'center',
    padding: spacing.md,
    minWidth: 80,
  },
  actionIcon: {
    marginBottom: spacing.xs,
  },
  actionText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.primary,
  },
  section: {
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.xl,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.light,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
  },
  seeAllText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.interactive.primary,
  },
  transactionsList: {
    paddingVertical: spacing.xs,
  },
  transactionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.light,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionMerchant: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text.primary,
  },
  transactionDate: {
    fontSize: 13,
    color: colors.text.tertiary,
    marginTop: spacing.xs,
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
  },
  transactionAmountPositive: {
    color: colors.status.success,
  },
  emptyTransactions: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
  },
  emptyTransactionsText: {
    fontSize: 15,
    color: colors.text.tertiary,
  },
});

export default VirtualCardScreen;
